import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { PrintSettingService } from './print-setting.service';
import { BaseRespone } from 'src/app/models/common.response';
import { IPrintSetting } from './iprint-setting';
import { IHospitalInfo } from '../../../hospital/ihospital-info';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';

@Component({
    selector: 'app-print-setting',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TablerIconsModule,
        MaterialModule,
        TranslateModule
    ],
    templateUrl: './print-setting.component.html',
    styleUrl: './print-setting.component.scss'
})
export class PrintSettingComponent implements OnInit, OnChanges {
    @Input() logoUrl: any;
    @Input() hospitalInfo: IHospitalInfo;
    @Output() saveSettings = new EventEmitter<void>();
    hospitalReportSettingForm: FormGroup;

    logoSizes = [
        { label: 'S (60)', value: '60px' },
        { label: 'M (100)', value: '100px' },
        { label: 'L (130)', value: '130px' },
        { label: 'XL (150)', value: '150px' }
    ];

    logoPositions = [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' }
    ];

    addressAlignments = [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' }
    ];
    doctorNamePositions = [
        { label: 'Top (Below Header)', value: 'top' },
        { label: 'Footer', value: 'footer' }
    ];

    constructor(
        private _formBuilder: FormBuilder,
        private _printSettingService: PrintSettingService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private translationUtils: TranslationUtils
    ) {
        // this.initForm();
    }

    ngOnInit(): void {
        this.hospitalReportSettingForm = this._formBuilder.group({
            id: [0],
            hospitalId: [],
            logoSize: ['150px'],
            logoPosition: ['right'],
            hospitalNameSize: [22],
            addressAlignment: ['left'],
            addressFontSize: [12],
            showFooter: [true],
            doctorNamePosition: ['footer']
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hospitalInfo'] && changes['hospitalInfo'].currentValue) {
            this.hospitalReportSettingForm.patchValue({
                hospitalId: this.hospitalInfo.id
            });
            this.fetchPrintSettings();
        }
    }

    resetToDefault() {
        this.hospitalReportSettingForm.patchValue({
            logoSize: '150px',
            logoPosition: 'right',
            hospitalNameSize: 22,
            addressAlignment: 'left',
            addressFontSize: 12,
            showFooter: true,
            doctorNamePosition: 'footer'
        });
    }

    onSave() {
        if (this.hospitalReportSettingForm.invalid) {
            this.hospitalReportSettingForm.markAllAsTouched();
            return;
        }

        const settings = this.hospitalReportSettingForm.value;
        const request = settings.id > 0
            ? this._printSettingService.updatePrintSetting(settings)
            : this._printSettingService.createPrintSetting(settings);

        request.subscribe((res: BaseRespone<IPrintSetting>) => {
            if (res.isSuccess) {
                const messageKey = settings.id > 0 ? 'COMMON.UPDATE' : 'COMMON.CREATE';
                this.toastr.success(this.translationUtils.getTranslatedMessage(messageKey, this.translate.instant('LABEL.PRINT_SETTINGS')));
                if (res.result && res.result.id) {
                    this.hospitalReportSettingForm.patchValue({ id: res.result.id });
                }
                this.saveSettings.emit();
            } else {
                const allErrors = res.errors;
                this.toastr.error(Array.isArray(allErrors) ? allErrors.join("\n") : String(allErrors));
            }
        });
    }

    fetchPrintSettings() {
        const settings = this.hospitalReportSettingForm.value;
        if (!settings.hospitalId) {
            return;
        }
        this._printSettingService.getPrintSettingByHospitalId(settings.hospitalId).subscribe((res: BaseRespone<IPrintSetting>) => {
            if (res.isSuccess && res.result) {
                this.hospitalReportSettingForm.patchValue(res.result);
            }
        });
    }

}
